import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Tag from '../ui/Tag';
import LuxuryButton from '../ui/LuxuryButton';
import { Reveal } from "../animations/Reveal";
import {
    FaFilter,
    FaTimes,
    FaChevronDown,
    FaChevronUp,
    FaSearch,
    FaSlidersH,
    FaTag,
    FaSortAmountDown,
    FaDollarSign,
    FaBars,
    FaCheck
} from 'react-icons/fa';

const FilterBarBit = ({
                          filters,
                          onFilterChange,
                          onClearFilter,
                          activeFilterCount,
                          categories = [],
                          concerns = [],
                          sortOptions = [
                              { id: 'popular', label: 'Most Popular' },
                              { id: 'newest', label: 'Newest' },
                              { id: 'price_low', label: 'Price: Low to High' },
                              { id: 'price_high', label: 'Price: High to Low' },
                              { id: 'rating', label: 'Highest Rated' }
                          ],
                          priceRanges = [
                              { min: 0, max: 50, label: 'Under $50' },
                              { min: 50, max: 100, label: '$50 - $100' },
                              { min: 100, max: 200, label: '$100 - $200' },
                              { min: 200, max: 500, label: '$200+' }
                          ],
                          loading = false
                      }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const Dropdown = ({ title, items, activeValue, onSelect, type = 'default', icon: Icon, mobile = false }) => (
        <div className="relative" ref={mobile ? null : dropdownRef}>
            <motion.button
                onClick={() => setActiveDropdown(activeDropdown === title ? null : title)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.95 }}
                className={`
                    flex items-center justify-between w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl
                    ${activeDropdown === title
                    ? 'bg-white border border-champagne/30 shadow-sm sm:shadow-medium'
                    : 'bg-white/80 backdrop-blur-sm border border-nude/50'
                }
                    transition-all duration-200 sm:duration-300
                `}
            >
                <div className="flex items-center gap-2 sm:gap-3">
                    {Icon && <Icon className="w-3 h-3 sm:w-4 sm:h-4 text-champagne" />}
                    <span className="font-medium text-sm sm:text-base text-charcoal/80">{title}</span>
                </div>
                {activeDropdown === title ?
                    <FaChevronUp className="w-3 h-3 text-charcoal/40" /> :
                    <FaChevronDown className="w-3 h-3 text-charcoal/40" />
                }
            </motion.button>

            <AnimatePresence>
                {activeDropdown === title && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className={`
                            absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-nude shadow-lg sm:shadow-lift z-50 p-3 sm:p-4
                            ${mobile ? 'relative mt-2 mb-4 shadow-inner' : ''}
                        `}
                    >
                        <div className="space-y-1 sm:space-y-2 max-h-60 sm:max-h-80 overflow-y-auto">
                            {type === 'price' ? (
                                items.map((range, index) => (
                                    <motion.button
                                        key={index}
                                        onClick={() => {
                                            onSelect(range);
                                            setActiveDropdown(null);
                                        }}
                                        whileHover={{ x: 5 }}
                                        className={`
                                            w-full text-left px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                                            ${filters.priceRange[0] === range.min &&
                                        filters.priceRange[1] === range.max
                                            ? 'bg-champagne/10 text-champagne'
                                            : 'hover:bg-nude/30 text-charcoal/60'
                                        }
                                            transition-colors duration-200 text-sm sm:text-base
                                            flex items-center justify-between
                                        `}
                                    >
                                        {range.label}
                                        {filters.priceRange[0] === range.min &&
                                            filters.priceRange[1] === range.max && (
                                                <FaCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                                            )}
                                    </motion.button>
                                ))
                            ) : (
                                items.map((item) => (
                                    <motion.button
                                        key={item.id}
                                        onClick={() => {
                                            onSelect(item.id);
                                            setActiveDropdown(null);
                                        }}
                                        whileHover={{ x: 5 }}
                                        className={`
                                            w-full text-left px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                                            ${activeValue === item.id
                                            ? 'bg-champagne/10 text-champagne'
                                            : 'hover:bg-nude/30 text-charcoal/60'
                                        }
                                            transition-colors duration-200 text-sm sm:text-base
                                            flex items-center justify-between
                                        `}
                                    >
                                        {item.label}
                                        {activeValue === item.id && (
                                            <FaCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                                        )}
                                    </motion.button>
                                ))
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    return (
        <>
            {/* Mobile Filter Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 sm:hidden"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: "spring", damping: 30 }}
                            className="fixed top-0 right-0 bottom-0 w-4/5 max-w-sm bg-white shadow-2xl z-50 sm:hidden overflow-y-auto"
                        >
                            <div className="p-4">
                                {/* Mobile Menu Header */}
                                <div className="flex items-center justify-between mb-6 pb-4 border-b border-nude/50">
                                    <h3 className="text-lg font-medium text-charcoal">Filters</h3>
                                    <button
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="p-2 hover:bg-nude/20 rounded-lg"
                                    >
                                        <FaTimes className="w-5 h-5 text-charcoal/60" />
                                    </button>
                                </div>

                                {/* Mobile Search */}
                                <div className="mb-6">
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-charcoal/40" />
                                        <input
                                            type="text"
                                            placeholder="Search products..."
                                            value={filters.searchQuery}
                                            onChange={(e) => onFilterChange('searchQuery', e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-white border border-nude rounded-xl focus:outline-none focus:border-champagne transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* Mobile Dropdowns */}
                                <div className="space-y-4 mb-6">
                                    <Dropdown
                                        title="Sort By"
                                        items={sortOptions}
                                        activeValue={filters.sortBy}
                                        onSelect={(id) => onFilterChange('sortBy', id)}
                                        icon={FaSortAmountDown}
                                        mobile={true}
                                    />

                                    <Dropdown
                                        title="Price Range"
                                        items={priceRanges}
                                        activeValue={filters.priceRange}
                                        onSelect={(range) => onFilterChange('priceRange', [range.min, range.max])}
                                        type="price"
                                        icon={FaDollarSign}
                                        mobile={true}
                                    />
                                </div>

                                {/* Mobile Active Filters */}
                                {activeFilterCount > 0 && (
                                    <div className="mb-6">
                                        <div className="flex items-center gap-2 mb-3">
                                            <FaTag className="w-4 h-4 text-champagne" />
                                            <span className="text-sm font-medium text-charcoal/60">Active Filters</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {filters.category !== 'all' && (
                                                <Tag
                                                    label={categories.find(c => c.id === filters.category)?.label}
                                                    active
                                                    removable
                                                    onRemove={() => onClearFilter('category')}
                                                    size="small"
                                                />
                                            )}

                                            {filters.concern !== 'all' && (
                                                <Tag
                                                    label={concerns.find(c => c.id === filters.concern)?.label}
                                                    active
                                                    removable
                                                    onRemove={() => onClearFilter('concern')}
                                                    size="small"
                                                />
                                            )}

                                            {(filters.priceRange[0] > 0 || filters.priceRange[1] < 500) && (
                                                <Tag
                                                    label={`$${filters.priceRange[0]} - $${filters.priceRange[1]}`}
                                                    active
                                                    removable
                                                    onRemove={() => onClearFilter('priceRange')}
                                                    size="small"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Mobile Filter Sections */}
                                <div className="space-y-6">
                                    {/* Categories */}
                                    <div>
                                        <h4 className="font-medium text-charcoal mb-3 text-sm">Category</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {categories.map((category) => (
                                                <Tag
                                                    key={category.id}
                                                    label={category.label}
                                                    active={filters.category === category.id}
                                                    onClick={() => onFilterChange('category', category.id)}
                                                    size="small"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* Concerns */}
                                    <div>
                                        <h4 className="font-medium text-charcoal mb-3 text-sm">Skin Concerns</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {concerns.map((concern) => (
                                                <Tag
                                                    key={concern.id}
                                                    label={concern.label}
                                                    active={filters.concern === concern.id}
                                                    onClick={() => onFilterChange('concern', concern.id)}
                                                    size="small"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* Mobile Clear All Button */}
                                    {activeFilterCount > 0 && (
                                        <div className="pt-4 border-t border-nude/50">
                                            <LuxuryButton
                                                variant="secondary"
                                                size="small"
                                                onClick={() => {
                                                    onClearFilter();
                                                    setIsMobileMenuOpen(false);
                                                }}
                                                icon={FaTimes}
                                                iconPosition="left"
                                                fullWidth
                                            >
                                                Clear All Filters
                                            </LuxuryButton>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Main Filter Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-nude/50">
                <div className="container-luxury">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 py-3 sm:py-4">
                        {/* Left: Filter Toggle & Search */}
                        <div className="flex items-center gap-3 sm:gap-4">
                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsMobileMenuOpen(true)}
                                className="sm:hidden flex items-center gap-2 px-3 py-2 bg-white border border-nude rounded-lg"
                            >
                                <FaBars className="w-4 h-4 text-charcoal/60" />
                                {activeFilterCount > 0 && (
                                    <span className="w-5 h-5 bg-champagne text-white text-xs rounded-full flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>

                            {/* Desktop Filter Toggle */}
                            <Reveal>
                                <motion.button
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="hidden sm:flex items-center gap-3 px-4 py-3 bg-champagne/10 hover:bg-champagne/20 rounded-xl transition-colors"
                                >
                                    <FaSlidersH className="w-4 h-4 text-champagne" />
                                    <span className="font-medium text-charcoal">Filters</span>
                                    {activeFilterCount > 0 && (
                                        <span className="w-6 h-6 bg-champagne text-white text-xs rounded-full flex items-center justify-center">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </motion.button>
                            </Reveal>

                            {/* Search - Responsive */}
                            <Reveal delay={0.1}>
                                <div className="relative flex-1 sm:flex-initial">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-charcoal/40" />
                                    <input
                                        type="text"
                                        placeholder="Search products..."
                                        value={filters.searchQuery}
                                        onChange={(e) => onFilterChange('searchQuery', e.target.value)}
                                        className="w-full sm:w-64 pl-10 pr-4 py-2.5 sm:py-3 bg-white/80 backdrop-blur-sm border border-nude rounded-lg sm:rounded-xl focus:outline-none focus:border-champagne transition-colors text-sm sm:text-base"
                                    />
                                </div>
                            </Reveal>
                        </div>

                        {/* Right: Sort & Clear - Desktop only */}
                        <div className="hidden sm:flex items-center gap-4">
                            <Reveal delay={0.2}>
                                <Dropdown
                                    title="Sort By"
                                    items={sortOptions}
                                    activeValue={filters.sortBy}
                                    onSelect={(id) => onFilterChange('sortBy', id)}
                                    icon={FaSortAmountDown}
                                />
                            </Reveal>

                            {activeFilterCount > 0 && (
                                <Reveal delay={0.3}>
                                    <LuxuryButton
                                        variant="secondary"
                                        size="small"
                                        onClick={onClearFilter}
                                        icon={FaTimes}
                                        iconPosition="left"
                                        className="whitespace-nowrap"
                                    >
                                        Clear All
                                    </LuxuryButton>
                                </Reveal>
                            )}
                        </div>
                    </div>

                    {/* Desktop Expanded Filters */}
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="border-t border-nude/50 pt-6 pb-8"
                            >
                                {/* Active Filters */}
                                {activeFilterCount > 0 && (
                                    <Reveal>
                                        <div className="mb-6">
                                            <div className="flex items-center gap-2 mb-3">
                                                <FaTag className="w-4 h-4 text-champagne" />
                                                <span className="text-sm font-medium text-charcoal/60">Active Filters</span>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {filters.category !== 'all' && (
                                                    <Tag
                                                        label={categories.find(c => c.id === filters.category)?.label}
                                                        active
                                                        removable
                                                        onRemove={() => onClearFilter('category')}
                                                    />
                                                )}

                                                {filters.concern !== 'all' && (
                                                    <Tag
                                                        label={concerns.find(c => c.id === filters.concern)?.label}
                                                        active
                                                        removable
                                                        onRemove={() => onClearFilter('concern')}
                                                    />
                                                )}

                                                {(filters.priceRange[0] > 0 || filters.priceRange[1] < 500) && (
                                                    <Tag
                                                        label={`$${filters.priceRange[0]} - $${filters.priceRange[1]}`}
                                                        active
                                                        removable
                                                        onRemove={() => onClearFilter('priceRange')}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Reveal>
                                )}

                                {/* Filter Sections */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                                    {/* Categories */}
                                    <Reveal delay={0.1}>
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-charcoal flex items-center gap-2">
                                                <FaTag className="w-4 h-4" />
                                                Category
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {categories.map((category) => (
                                                    <Tag
                                                        key={category.id}
                                                        label={category.label}
                                                        active={filters.category === category.id}
                                                        onClick={() => onFilterChange('category', category.id)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </Reveal>

                                    {/* Concerns */}
                                    <Reveal delay={0.2}>
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-charcoal flex items-center gap-2">
                                                <FaFilter className="w-4 h-4" />
                                                Skin Concerns
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {concerns.map((concern) => (
                                                    <Tag
                                                        key={concern.id}
                                                        label={concern.label}
                                                        active={filters.concern === concern.id}
                                                        onClick={() => onFilterChange('concern', concern.id)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </Reveal>

                                    {/* Price */}
                                    <Reveal delay={0.3}>
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-charcoal flex items-center gap-2">
                                                <FaDollarSign className="w-4 h-4" />
                                                Price Range
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {priceRanges.map((range, index) => (
                                                    <Tag
                                                        key={index}
                                                        label={range.label}
                                                        active={filters.priceRange[0] === range.min &&
                                                            filters.priceRange[1] === range.max}
                                                        onClick={() => onFilterChange('priceRange', [range.min, range.max])}
                                                    />
                                                ))}
                                            </div>

                                            {/* Price Slider */}
                                            <div className="space-y-2 pt-4">
                                                <div className="flex justify-between text-sm text-charcoal/60">
                                                    <span>${filters.priceRange[0]}</span>
                                                    <span>${filters.priceRange[1]}</span>
                                                </div>
                                                <div className="relative h-2">
                                                    <div className="absolute inset-0 bg-nude rounded-full" />
                                                    <motion.div
                                                        className="absolute h-full bg-gradient-to-r from-champagne to-sage rounded-full"
                                                        animate={{
                                                            left: `${(filters.priceRange[0] / 500) * 100}%`,
                                                            width: `${((filters.priceRange[1] - filters.priceRange[0]) / 500) * 100}%`
                                                        }}
                                                        transition={{ duration: 0.3 }}
                                                    />
                                                    <input
                                                        type="range"
                                                        min="0"
                                                        max="500"
                                                        value={filters.priceRange[0]}
                                                        onChange={(e) => onFilterChange('priceRange', [
                                                            parseInt(e.target.value),
                                                            filters.priceRange[1]
                                                        ])}
                                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                                    />
                                                    <input
                                                        type="range"
                                                        min="0"
                                                        max="500"
                                                        value={filters.priceRange[1]}
                                                        onChange={(e) => onFilterChange('priceRange', [
                                                            filters.priceRange[0],
                                                            parseInt(e.target.value)
                                                        ])}
                                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Reveal>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </>
    );
};

export default FilterBarBit;